import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs';

import * as catCodes from '@shared-constants/cat-codes-constants';
import { dayOfWeekToNumberLookup } from '@shared-constants/date-constants';
import { DATE_FORMAT_TOR } from '@shared-constants/date-format-constants';
import { BasicInterfaceTypeEnum, CatsNoBillCodeDescriptionEnum, CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { PayrollTypeFullEnum } from '@shared-enums/time.enum';
import { TorManagerApprovalStatusEnum } from '@shared-enums/tor.enum';
import { arrayIsTruthy, findUnique, removeDuplicates, syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, convertSapDateToWeekDayString, getDayString, isSameDay } from '@shared-helpers/date-helpers';
import { addDecimals, removeFirstChar, safeCompare, toCurrencyString } from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IDayOfWeek, IWeekDayNumbers } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import {
  IEmployeeBillingSummaryRow,
  IEmployeePayrollSummaryRow,
  IEmployeePayrollSummaryRowDay,
  ISelectOptions,
  ITableConfigEntity,
  ITextClickEvent,
  ITotal,
  IWeeklyTotal,
} from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';
import { ModalService } from '../services/modal.service';
import {
  BasicInterface,
  IAnyInterfaceType,
  IBasicInterfaceContext,
  IBasicInterfaceContract,
  IBasicInterfaceNotProductionTextContract,
} from './basicInterface';
import { IOutgoingCatsArrays } from './catsDB';
import { EmployeeCatsRecord } from './employeeCatsRecord';
import { NonbillableCatsRecord } from './NonbillableCatsRecord';
import { PayrollSimulator } from './PayrollSimulator';
import { PerDiemCatsRecord } from './PerDiemCatsRecord';
import { makeBillingEmployeeDailyConfig } from './table-configs/billing-tables.config';
import {
  makePayrollEmployeeDailyConfig,
  makePayrollEmployeePerDiemConfig,
  makePayrollEmployeeSummaryConfig,
  torDailyConfig,
} from './table-configs/payroll-tables.config';
import { ITimesheetDate } from './timesheetHandler';

export class Employee extends BasicInterface implements IBasicInterfaceContract, IBasicInterfaceNotProductionTextContract {
  //this class handles logic and display for employees in the application.
  //todo: should indicate if this is a billing or payroll employee, to not waste time rendering the grids that are not needed.

  //calculated or stateful fields
  public display = '';
  public tooltip = '';
  public name = '';
  public activityList: ISelectOptions[] = [];
  public detailPayneRecords: Array<EmployeeCatsRecord | NonbillableCatsRecord | PerDiemCatsRecord> = [];
  public detailPayneNonbillableRecords: NonbillableCatsRecord[];
  public detailPaynePerDiemRecords: PerDiemCatsRecord[];
  public readonly payrollConfig: ITableConfigEntity[] = makePayrollEmployeeSummaryConfig(); //This is accessed via a string index lookup with the table's `configPath` property - do not change this property name!
  public payrollSimulator: PayrollSimulator = new PayrollSimulator();
  public total = '0';
  public payrollTotals: IWeeklyTotal = this.makeTotals();
  public readonly billCodeType = BasicInterfaceTypeEnum.Employee;

  //These appear to be private but they are "magically" accessed by a string path to this property name!
  public payrollGrid: IEmployeePayrollSummaryRow[] = []; //this array gives values to the summary table for the employee payroll view.
  public billingGrid: IEmployeeBillingSummaryRow[] = [];

  //Fields provided from employee API, values get assigned to these properties by magic somewhere else, and are sometimes accessed by magic string paths, so they have to remain public!
  public EmpToActivity: { results: sharedApi.IEmployeeActivity[] };
  public CostCenter = '';
  public EmpId = '';
  public FirstName = '';
  public LastName = '';
  public Default = '';
  public ServiceLine = '';
  public SortName = '';
  public listener = '';
  public CCWeek = '';
  public DAWeek = '';
  public InvalidDates = '';
  public TimeMngtCode = '';
  public TimeMngtText = '';
  public WorkScheduleRule = '';
  public perDiemRecords: ICatRecord[] = [];

  //These are only used here within this class
  private nonBillRecords: ICatRecord[] = [];
  private torRecords: sharedApi.IPayrollTimeOffRequestResult[] = [];
  private billableRecords: ICatRecord[] = [];

  constructor(
    employeeRecord: sharedApi.IHttpResponseEmployee,
    public readonly datahub: DatahubService, //TODO: This should be private or protected! If other things need datahub they should not pull it off of an Employee object!
    private readonly modalService: ModalService,
    context: IBasicInterfaceContext,
  ) {
    super(context);
    syncObjectProperties(employeeRecord, this, false);
    this.setDisplayProperty();
    this.getActivityList();
  }

  public setCatsRecords(catsRecords: ICatRecord[]): void {
    this.catsRecords = catsRecords;
    this.visibleCatsRecords = catsRecords.filter(
      (x) => Number(x.Catshours) > 0 && x.AbsAttType !== catCodes.OVERTIME_AUTOMATION_OVERRIDE_CODE,
    );
    this.nonBillRecords = this.visibleCatsRecords.filter(
      (x) => !x.Acttype && x.Zrecordtype !== CatsRecordTypeEnum.EmployeePerDiem,
    );
    this.perDiemRecords = this.catsRecords.filter((x) => x.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem);
    this.billableRecords = this.visibleCatsRecords.filter((x) => !!x.Acttype);
    if (!this.isPayroll && !arrayIsTruthy(this.billableRecords)) {
      this.shouldNeverShow = true;
    } else {
      this.handleCatsRecordsSet();
    }
  }

  public handleTorRecordsSet(
    torEmployee: sharedApi.IPayrollTimeOffRequestResultSetResult,
  ): sharedApi.IPayrollTimeOffRequestResult[] {
    //we do not render the payroll immediately. This function must be called before cats record set.
    this.torRecords = [];
    torEmployee.TorResult?.results?.forEach((record: sharedApi.IPayrollTimeOffRequestResult) => {
      if (record.Mgrapprstat === TorManagerApprovalStatusEnum.Approved && !record.Cashout) {
        if (record.Begda === record.Endda) {
          this.torRecords.push(record);
        } else {
          const begin = moment(record.Begda, DATE_FORMAT_TOR);
          const end = moment(record.Endda, DATE_FORMAT_TOR);
          let days: number = end.diff(begin, 'days') + 1;
          const amount = Number(record.Abshours) / days;
          record.Abshours = String(amount);
          while (days > 0) {
            const copy = structuredClone(record);
            const newDate = begin
              .clone()
              .add(days - 1, 'days')
              .format(DATE_FORMAT_TOR);
            copy.Begda = newDate;
            copy.Endda = newDate;
            this.torRecords.push(copy);
            days--;
          }
        }
      }
    });
    return this.torRecords;
  }

  public filterByTimesheet(timesheetNo: string): void {
    this.show();
    this.shouldNeverShow = false; //TODO: Why the f are we overriding this property here????
    this.filteredTimesheet = timesheetNo;
    this.calculateBillingGrid(timesheetNo);
    if (!arrayIsTruthy(this.billingGrid)) this.hide();
  }

  public getTorConfig(): ITableConfigEntity[] {
    return torDailyConfig;
  }

  public getTorValues(day: IDayOfWeek): sharedApi.IPayrollTimeOffRequestResult[] {
    const values: sharedApi.IPayrollTimeOffRequestResult[] = this.torRecords.filter((x) => {
      const workDateAsMoment = moment(x.Begda, DATE_FORMAT_TOR);
      const weekDay = getDayString(workDateAsMoment);
      return day === weekDay;
    });
    // if(values.length == 1) values.push({})
    return values;
  }

  public addNewRecord(config: ITableConfigEntity[]): EmployeeCatsRecord {
    const record = new EmployeeCatsRecord(this, config);
    return record.getRequiredObject(this);
  }

  public addNewNonbillableRecord(timesheetNo: string, date?: ITimesheetDate): NonbillableCatsRecord {
    const record = new NonbillableCatsRecord(this.datahub.nonBillable);
    return record.getRequiredObject(this, timesheetNo, date);
  }

  public addNewPerDiemRecord(timesheetNo: string, date?: ITimesheetDate): PerDiemCatsRecord {
    const record = new PerDiemCatsRecord();
    return record.getRequiredObject(this, timesheetNo, date);
  }

  public handleMassBillCodeChange$(current: string, desired: string, timesheetNo: string): Observable<ICatRecord[]> {
    let records = this.catsRecords.filter((x) => x.Zmatnr === current);
    if (timesheetNo) records = records.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    const update = records.map((x) => {
      return {
        Zmatnr: desired,
        Counter: x.Counter,
      };
    });
    const object: IOutgoingCatsArrays = {
      create: [],
      delete: [],
      update: update,
      all: update,
    };
    return this.save$(object);
  }

  public save$(records: IOutgoingCatsArrays): Observable<ICatRecord[]> {
    return this.datahub.catsDB.syncSingleEntityWithCats$(records).pipe(
      tap((catsRecords: ICatRecord[]) => {
        records.update.concat(records.delete).forEach((record) => {
          this.catsRecords = this.catsRecords.filter((x) => x.Counter !== record.Counter);
        });
        this.catsRecords = this.catsRecords.concat(catsRecords);
        this.setCatsRecords(this.catsRecords);
      }),
      switchMap(() => {
        if (!this.isPayroll) return of(this.catsRecords);
        return this.datahub.payrollTimesheet.getPayrollSimulation$(this.catsRecords, this.torRecords).pipe(
          map((timeEvalItem) => {
            if (timeEvalItem != null) {
              //If no change was made and you save/accept anyway, you can get a null value back which would error out here otherwise
              const self = timeEvalItem.find((y) => safeCompare(y.Pernr, this.EmpId));
              if (self) this.compareTimeEval(self);
            }
            return this.catsRecords;
          }),
        );
      }),
    );
  }

  public showBulkAdd(): void {
    this.focusedDay = undefined;
    let config;
    if (this.isPayroll) config = this.getPayrollDetailPayneConfig();
    else config = this.getBillingDetailPayneConfig();
    this.datahub.detailPayneHandler.showBulkAdd(this, config, this.isPayroll);
  }

  public handlePerDiemTotalClicked(event: ITextClickEvent<IAnyInterfaceType>): void {
    if (event.entity instanceof Employee) {
      this.modalService
        .launchResponsiveModal('per-diem-totals', {
          employeeInfo: event.entity,
        })
        .afterClosed()
        .subscribe({
          next: () => {
            this.datahub.detailPayneHandler.showSummary();
          },
          error: (err) => {
            console.error('Error in handlePerDiemTotalClicked:', err);
          },
        });
    }
  }

  public launchPayrollDetailPayneHandler(row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    this.focusedDay = this.datahub.payrollTimesheet.getDate(column.key);
    if (this.isInvalid(this.focusedDay.number)) this.datahub.detailPayneHandler.showInvalidEntity(this, 'employee');
    else this.getPayrollDetailPayneValues(row, column);
  }

  public getPayrollDetailPayneValues(row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    // if(this.InvalidDates.includes(this.focusedDay.number)) return this.datahub.detailPayneHandler.showInvalidEmployee(this)
    const activity = this.payrollGrid[row]?.activityNumber ?? '';
    if (activity === 'tor') {
      if (this.payrollGrid[row]?.[column.key].s) {
        this.datahub.detailPayneHandler.showTorRecords(this);
        return;
      }
    }
    const isNonbill = activity === 'non';
    const isPerDiem = activity === 'EPD';
    const records = this.billableRecords.filter((record) => removeFirstChar(activity) === removeFirstChar(record.Acttype));

    const employeeRecords = records
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new EmployeeCatsRecord(this, this.getPayrollDetailPayneConfig(column.key, x)).fillStateFromSap(x));

    this.detailPayneNonbillableRecords = this.nonBillRecords
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new NonbillableCatsRecord(this.datahub.nonBillable).fillStateFromSap(x));

    this.detailPaynePerDiemRecords = this.perDiemRecords
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new PerDiemCatsRecord().fillStateFromSap(x));

    this.detailPayneRecords = [...employeeRecords, ...this.detailPayneNonbillableRecords, ...this.detailPaynePerDiemRecords];
    this.datahub.detailPayneHandler.showPayrollEmployee(this, isNonbill, isPerDiem);
  }

  public getBillingDetailPayneValues(row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    this.focusedDay = this.datahub.billingTimesheet.getDate(column.key);
    if (this.isInvalid(this.focusedDay.number)) {
      this.datahub.detailPayneHandler.showInvalidEntity(this, 'Employee');
      return;
    }
    const material = this.billingGrid[row]?.material ?? '';
    const records = this.billableRecords.filter((record) => material === record.Zmatnr);
    this.detailPayneRecords = records
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new EmployeeCatsRecord(this, this.getBillingDetailPayneConfig(x)).fillStateFromSap(x));
    this.datahub.detailPayneHandler.showBillingEmployee(this);
  }

  /**
   * @description When the detail payne is rendered for this employee, this function provides the appropriate drop down values.
   * @todo The `day` param should probably be either the day of week name OR the number, not both!
   */
  public getPayrollDetailPayneConfig(day?: IDayOfWeek | IWeekDayNumbers, record?: ICatRecord): ITableConfigEntity[] {
    let config: ITableConfigEntity[];
    if (typeof day === 'string') {
      const dayAsNumber = dayOfWeekToNumberLookup[day];
      config = makePayrollEmployeeDailyConfig(this.getActivityList(dayAsNumber));
    } else {
      config = makePayrollEmployeeDailyConfig(this.getActivityList(day));
    }

    if (record) {
      return (
        this.datahub.payrollTimesheet.extDocuments
          .find((x) => safeCompare(x.Documentno, record.Extdocumentno))
          ?.makeForeignConfig(config) ?? []
      );
    } else {
      return config;
    }
  }

  public getBillingDetailPayneConfig(record?: ICatRecord): ITableConfigEntity[] {
    const config = makeBillingEmployeeDailyConfig();
    if (record) {
      return (
        this.datahub.billingTimesheet.extDocuments
          .find((x) => safeCompare(x.Documentno, record.Extdocumentno))
          ?.makeForeignConfig(config) ?? []
      );
    } else {
      return config;
    }
  }

  public generateBillcodeDefaultInterface(
    custGrp: string,
    condGrp: string,
    billcode: string,
    activity?: string,
  ): sharedApi.IConditionToDefault {
    return {
      CustGroup: custGrp,
      CondGroup: condGrp,
      Pernr: this.EmpId,
      EquipmentId: '',
      BillCode: billcode,
      Activity: activity,
    };
  }

  public isInvalid(day: IWeekDayNumbers): boolean {
    return this.InvalidDates.includes(String(day));
  }

  public generateOvertimeTypesFromActivityType(activityCode: string): IActivityCodeObject[] {
    if (activityCode.length !== 5) throw Error('Activity codes are 5 digit numbers.');
    const lastFour = activityCode.substring(1);
    const straight = this.makeActivityCodeObject('2', lastFour, 'ST');
    const overtime = this.makeActivityCodeObject('5', lastFour, 'OT');
    const doubleTime = this.makeActivityCodeObject('6', lastFour, 'DT');
    return [straight, overtime, doubleTime];
  }

  public compareTimeEval(timeEvalEmployee: sharedApi.IPayrollTimeEvalResult): void {
    const overrideRecord = this.catsRecords.find(
      (x) => x.AbsAttType === catCodes.OVERTIME_AUTOMATION_OVERRIDE_CODE && Number(x.Catshours) > 0,
    );
    this.payrollSimulator.handleTimeEvalSet(timeEvalEmployee, this.payrollTotals, overrideRecord);
    const totalConfig = this.payrollConfig.find((x) => x.key === 'total');
    if (totalConfig) {
      if (this.payrollSimulator.isPayrollAutomationOverridden) {
        //automation takes precedence, since now we project automation after override.
        totalConfig.class = 'time-eval-has-issue time-eval-override-payroll-automation';
        totalConfig.tooltip = 'Overriding Payroll Automation.';
      } else if (this.payrollSimulator.isInSync) {
        //https://en.wikipedia.org/wiki/NSYNC
        totalConfig.class = '';
        totalConfig.tooltip = '';
      } else {
        totalConfig.class = 'time-eval-has-issue time-eval-out-of-sync-with-payroll';
        totalConfig.tooltip = 'Out of Sync with payroll.';
      }
    }
  }

  public handleCatsRecordsSet(): void {
    //We could probably declare the entity's context on creation instead of determining each time.
    if (this.isPayroll) this.calculatePayrollGrid();
    else this.calculateBillingGrid(this.filteredTimesheet);
    this.checkApprovalStatus(this.catsRecords);
  }

  public calculateBillingGrid(timesheetNo?: string): void {
    let recordPool = this.billableRecords;
    if (timesheetNo) recordPool = recordPool.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    const codes = findUnique<string>(recordPool, 'Zmatnr', true);
    this.total = '0';
    this.billingGrid = codes.map((x) => {
      return this.makeBillingRow(x, recordPool);
    });
  }

  public calculatePayrollGrid(): void {
    this.payrollGrid = [];
    let activities = findUnique<string>(this.billableRecords, 'Acttype');
    activities = this.dropVariants(activities); //drop double time and overtime duplicates, prioritizing straight time.
    this.total = '0';
    this.payrollTotals = this.makeTotals();
    this.payrollGrid = activities.map((x) => {
      return this.makeActivityRow(x);
    });
    if (arrayIsTruthy(this.nonBillRecords)) this.payrollGrid.push(this.makeNonbillRow());
    if (arrayIsTruthy(this.torRecords)) this.payrollGrid.push(this.makeTorRow(this.torRecords));
    if (this.payrollGrid.length > 0) {
      //if we have values, then set all the totals
      this.payrollGrid.forEach((x) => {
        x.total = this.total;
        x.s = this.payrollTotals.s.total;
        x.o = this.payrollTotals.o.total;
        x.d = this.payrollTotals.d.total;
      });
    } else {
      //If we have no values, then make an empty row
      this.payrollGrid = [this.makeEmptyEmployeePayrollSummaryRow('', '')];
    }
    //Add a per diem row last if we need it, otherwise
    if (this.perDiemRecords.length > 0) {
      const perDiemRow = this.makePerDiemRow();
      if (perDiemRow) {
        this.payrollGrid.push(perDiemRow);
      }
    }
  }

  public makeBillingRow(code: string, recordPool: ICatRecord[]): IEmployeeBillingSummaryRow {
    const row = this.makeEmptyEmployeeBillingSummaryRow(code);
    const records = recordPool.filter((x) => x.Zmatnr === code);
    records.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const hours = Number(record.Catshours); //can't perform operations over strings.
      row[weekDay] = addDecimals(row[weekDay], hours);
      row.total = addDecimals(row.total, hours);
      this.total = addDecimals(this.total, hours);
    });
    return row;
  }

  public hasPerDiemRecordsForFocusedDay(): boolean {
    return this.perDiemRecords.some(
      (pdr) =>
        Number(pdr.Zperdiemamount) > 0 &&
        this.focusedDay != null &&
        isSameDay(convertSapDateToMoment(pdr.Workdate), this.focusedDay.moment),
    );
  }

  private setDisplayProperty(): void {
    this.display = `${this.FirstName} ${this.LastName} - ${this.EmpId}`;
    this.name = `${this.LastName}, ${this.FirstName}`;
    this.tooltip = this.EmpId;
  }

  private updatePayrollTotals(type: PayrollTypeFullEnum, value: number, weekDay: IDayOfWeek): void {
    this.total = addDecimals(this.total, value);
    const totalForType = this.payrollTotals[type];
    if (totalForType != null) {
      totalForType[weekDay] = addDecimals(totalForType[weekDay], value);
      totalForType.total = addDecimals(totalForType.total, value);
    }
  }

  private makeTotals(): IWeeklyTotal {
    const emptyTotal: ITotal = {
      total: '0',
      sun: '0',
      mon: '0',
      tue: '0',
      wed: '0',
      thu: '0',
      fri: '0',
      sat: '0',
    };

    return {
      s: structuredClone(emptyTotal),
      o: structuredClone(emptyTotal),
      d: structuredClone(emptyTotal),
      // h: structuredClone(emptyTotal),
      u: structuredClone(emptyTotal),
    };
  }

  private makeNonbillRow(): IEmployeePayrollSummaryRow {
    const row = this.makeEmptyEmployeePayrollSummaryRow('Nonbillable', 'non');
    this.nonBillRecords.forEach((record) => {
      if (record.AbsAttType === catCodes.OVERTIME_AUTOMATION_OVERRIDE_CODE) return;
      const type = this.datahub.nonBillable.getType(record.AbsAttType);
      const sod = this.datahub.nonBillable.getSOD(record.AbsAttType); //if they want to show unpaid or nonbill differently then straight, start here.
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const hours = Number(record.Catshours);
      row[weekDay][sod] = addDecimals(row[weekDay][sod], hours); //only using sod right now for possible time types.
      row.total = addDecimals(row.total, hours);
      this.updatePayrollTotals(type, hours, weekDay);
    });
    return row;
  }

  private makeActivityRow(activity: string): IEmployeePayrollSummaryRow {
    //render a single object for each activity type.
    const description = this.getActivityDescription(activity);
    const row = this.makeEmptyEmployeePayrollSummaryRow(description, activity);
    const records = this.billableRecords.filter((record) => removeFirstChar(record.Acttype) === removeFirstChar(activity));
    records.forEach((record) => {
      const type = this.determineWorkType(record);
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const hours = Number(record.Catshours); //can't perform operations over strings.
      row[weekDay][type] = addDecimals(row[weekDay][type] ?? 0, hours);
      row.total = addDecimals(row.total, hours);
      this.updatePayrollTotals(type, hours, weekDay);
    });
    return row;
  }

  private makeTorRow(torEntries: sharedApi.IPayrollTimeOffRequestResult[]): IEmployeePayrollSummaryRow {
    const row = this.makeEmptyEmployeePayrollSummaryRow('TOR Absence', 'tor');
    torEntries.forEach((entry) => {
      const workDateAsMoment = moment(entry.Begda, DATE_FORMAT_TOR);
      const weekDay = getDayString(workDateAsMoment);
      const hours = Number(entry.Abshours); //can't perform operations over strings.
      row[weekDay].s = addDecimals(row[weekDay].s, hours);
      row.total = addDecimals(row.total, hours);
      this.updatePayrollTotals(PayrollTypeFullEnum.Straight, hours, weekDay);
    });
    return row;
  }

  private makePerDiemRow(): IEmployeePayrollSummaryRow | undefined {
    const row = this.makeEmptyEmployeePayrollSummaryRow('', CatsRecordTypeEnum.EmployeePerDiem);
    row.employee = 'Per Diem ($)'; //use this as the "employee name" instead
    row.total = '0.00'; //default to zero dollars. We don't want it to use the other total and add hours and dollars together!
    this.perDiemRecords.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const total = Number(record.Zperdiemamount);
      row[weekDay][PayrollTypeFullEnum.Straight] = addDecimals(row[weekDay][PayrollTypeFullEnum.Straight], total);
      row.total = addDecimals(row.total, total);
    });
    if (Number(row.total) > 0) {
      row.total = toCurrencyString(row.total, true);
      row.rowSpanOverride = 5; //normal rows span 4, these will be slightly larger
      row.configOverride = makePayrollEmployeePerDiemConfig(); //override the column config so we can style this row differently
      return row;
    } else {
      return undefined;
    }
  }

  private getActivityDescription(activity: string): string {
    const found = this.activityList.find((x) => safeCompare(x.value, activity));
    if (found) return found.description;
    return '';
  }

  /** @Description This function transforms the raw activity array from sap to match the internal interface requirements and strip some layers without mutating original data. */
  private getActivityList(day?: IWeekDayNumbers): ISelectOptions[] {
    if (day == null) {
      this.activityList = this.makeActivityList(this.EmpToActivity.results);
    } else {
      const activities = this.EmpToActivity.results.filter((entity) => !entity.InvalidDates.includes(String(day)));
      this.activityList = this.makeActivityList(activities);
    }
    return this.activityList;
  }

  private determineWorkType(laborCatsRecord: ICatRecord): PayrollTypeFullEnum {
    //I got this from tribal knowledge, not 100% about this.
    if (laborCatsRecord.AbsAttType === catCodes.STRAIGHT_TIME_ATTENDANCE_CODE) return PayrollTypeFullEnum.Straight;
    if (
      laborCatsRecord.AbsAttType === catCodes.OVERTIME_ATTENDANCE_CODE ||
      laborCatsRecord.AbsAttType === catCodes.SPECIAL_ACCRUAL_OVERTIME_ATTENDANCE_CODE
    ) {
      return PayrollTypeFullEnum.Overtime;
    }
    if (
      laborCatsRecord.AbsAttType === catCodes.DOUBLE_TIME_ATTENDANCE_CODE ||
      laborCatsRecord.AbsAttType === catCodes.SPECIAL_ACCRUAL_DOUBLE_TIME_ATTENDANCE_CODE
    ) {
      return PayrollTypeFullEnum.Double;
    }
    throw new Error('Unhandled record type, probably nonbill or PTO');
  }

  private makeEmptyEmployeeBillingSummaryRow(material: string): IEmployeeBillingSummaryRow {
    let billcode = material;
    if (!billcode) billcode = CatsNoBillCodeDescriptionEnum.Unassigned;
    return {
      employee: this.name,
      material: material,
      billCode: billcode,
      total: '0',
      sun: '0',
      mon: '0',
      tue: '0',
      wed: '0',
      thu: '0',
      fri: '0',
      sat: '0',
    };
  }

  private makeEmptyEmployeePayrollSummaryRow(activity: string, activityNumber: string): IEmployeePayrollSummaryRow {
    //provides the default values for the employee payroll summary view.
    return {
      employee: this.name,
      activity: activity,
      activityNumber: activityNumber,
      total: this.total,
      s: '0',
      o: '0',
      d: '0',
      sun: this.makeEmptyDay(),
      mon: this.makeEmptyDay(),
      tue: this.makeEmptyDay(),
      wed: this.makeEmptyDay(),
      thu: this.makeEmptyDay(),
      fri: this.makeEmptyDay(),
      sat: this.makeEmptyDay(),
    };
  }

  private makeActivityCodeObject(singleDigitNumber: string, lastFour: string, description: string): IActivityCodeObject {
    return { description, singleDigitNumber, code: singleDigitNumber + lastFour };
  }

  private dropVariants(array: string[]): string[] {
    if (!arrayIsTruthy(array)) return [];
    const noFirst = array.map((x) => removeFirstChar(x)); //compare values without their first char.
    const noDupes = removeDuplicates(noFirst); //kill the duplicates
    const fixed = noDupes.map((x) => `2${x}`); //keep only straight time versions.
    return fixed;
  }

  private makeActivityList(activityArray: sharedApi.IEmployeeActivity[]): ISelectOptions[] {
    return activityArray.map((entity) => {
      return {
        value: entity.ActivityType,
        description: entity.Description,
      };
    });
  }

  private makeEmptyDay(): IEmployeePayrollSummaryRowDay {
    return { s: '0', o: '0', d: '0', n: '0', p: '0' };
  }
}

interface IActivityCodeObject {
  description: string;
  code: string;
  singleDigitNumber: string;
}
